/**
 * Artifact Download Utilities
 *
 * Funções otimizadas para download de artefatos
 * - Download direto por tipo nativo (sem conversão)
 * - Conversão HTML para PDF usando html2canvas + jspdf
 * - Limpeza automática de URLs de blob
 * - Geração segura de nomes de arquivo
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  HeadingLevel,
} from 'docx';

import {
  ARTIFACT_TYPES,
  DOWNLOAD_FORMATS,
  MIME_TYPES,
  NATIVE_FORMAT_MAP,
  type DownloadFormat,
} from '../constants';

// =============================================================================
// TYPES
// =============================================================================

export interface Artifact {
  id: string;
  type: string;
  title: string;
  content: string;
  language?: string;
  updatedAt?: string;
}

export interface DownloadResult {
  success: boolean;
  filename?: string;
  error?: string;
}

// =============================================================================
// FILENAME GENERATION
// =============================================================================

/**
 * Gera nome de arquivo seguro e legível
 * Remove caracteres especiais, limita tamanho, adiciona timestamp
 */
export const generateFileName = (title: string, format: DownloadFormat): string => {
  const cleanTitle = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
    .replace(/\s+/g, '-') // Espaços para hífens
    .replace(/-+/g, '-') // Remove hífens duplicados
    .substring(0, 50)
    .replace(/^-|-$/g, ''); // Remove hífens no início/fim

  const timestamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[T:]/g, '-');

  return `${cleanTitle || 'artifact'}-${timestamp}.${format}`;
};

// =============================================================================
// CORE DOWNLOAD FUNCTION
// =============================================================================

/**
 * Faz download de um blob com limpeza automática de memória
 */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Limpeza imediata após click
  requestAnimationFrame(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};

// =============================================================================
// DOWNLOAD BY FORMAT
// =============================================================================

/**
 * Download como texto simples
 */
export const downloadAsText = (artifact: Artifact): DownloadResult => {
  try {
    const blob = new Blob([artifact.content], { type: MIME_TYPES.txt });
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.TXT);
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como TXT',
    };
  }
};

/**
 * Download como Markdown
 */
export const downloadAsMarkdown = (artifact: Artifact): DownloadResult => {
  try {
    const blob = new Blob([artifact.content], { type: MIME_TYPES.md });
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.MD);
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como MD',
    };
  }
};

/**
 * Download como HTML
 */
export const downloadAsHtml = (artifact: Artifact): DownloadResult => {
  try {
    const blob = new Blob([artifact.content], { type: MIME_TYPES.html });
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.HTML);
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como HTML',
    };
  }
};

/**
 * Download como CSV
 */
export const downloadAsCsv = (artifact: Artifact): DownloadResult => {
  try {
    const blob = new Blob([artifact.content], { type: MIME_TYPES.csv });
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.CSV);
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como CSV',
    };
  }
};

/**
 * Verifica se o conteúdo é HTML
 */
const isHtmlContent = (content: string): boolean => {
  const trimmed = content.trim().toLowerCase();
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<head') ||
    trimmed.startsWith('<body') ||
    (trimmed.includes('<html') && trimmed.includes('</html>'))
  );
};

/**
 * Constantes para tamanho A4
 */
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 10;

/**
 * Converte HTML para PDF usando abordagem simples:
 * 1. Renderiza HTML completo em um canvas
 * 2. Divide o canvas em fatias do tamanho de uma página A4
 * 3. Cada fatia vira uma página do PDF
 *
 * Não tenta ser inteligente com quebras - apenas divide linearmente.
 */
const convertHtmlToPdf = async (
  htmlContent: string,
  filename: string
): Promise<DownloadResult> => {
  // Largura em pixels para renderização (boa qualidade)
  const renderWidthPx = 800;

  // Cria iframe para isolar estilos e renderizar o HTML
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `
    position: fixed;
    left: -9999px;
    top: 0;
    width: ${renderWidthPx}px;
    height: 2000px;
    border: none;
    visibility: hidden;
  `;

  document.body.appendChild(iframe);

  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      throw new Error('Não foi possível criar iframe para renderização');
    }

    // Escreve o HTML no iframe
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    // Aguarda carregamento (imagens, fontes, CSS)
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Ajusta altura do iframe para conter todo o conteúdo
    const totalHeight = Math.max(
      iframeDoc.body.scrollHeight,
      iframeDoc.documentElement.scrollHeight,
      1000
    );
    iframe.style.height = `${totalHeight + 100}px`;

    // Aguarda re-render
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Captura TODO o documento como um único canvas
    const fullCanvas = await html2canvas(iframeDoc.body, {
      scale: 2, // Alta resolução
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: renderWidthPx,
      windowWidth: renderWidthPx,
    });

    // Dimensões do PDF
    const contentWidthMm = A4_WIDTH_MM - MARGIN_MM * 2;
    const contentHeightMm = A4_HEIGHT_MM - MARGIN_MM * 2;

    // Calcula a escala: canvas -> PDF
    const pxPerMm = fullCanvas.width / contentWidthMm;
    const pageHeightPx = contentHeightMm * pxPerMm;

    // Número de páginas necessárias
    const numPages = Math.ceil(fullCanvas.height / pageHeightPx);

    // Cria o PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Gera cada página
    for (let page = 0; page < numPages; page++) {
      if (page > 0) {
        pdf.addPage();
      }

      // Calcula a região do canvas para esta página
      const sourceY = page * pageHeightPx;
      const sourceHeight = Math.min(pageHeightPx, fullCanvas.height - sourceY);

      if (sourceHeight <= 0) continue;

      // Cria um canvas temporário para esta fatia
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = fullCanvas.width;
      pageCanvas.height = sourceHeight;

      const ctx = pageCanvas.getContext('2d');
      if (!ctx) continue;

      // Copia a fatia do canvas original
      ctx.drawImage(
        fullCanvas,
        0, sourceY, fullCanvas.width, sourceHeight, // source
        0, 0, fullCanvas.width, sourceHeight // destination
      );

      // Adiciona ao PDF
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.92);
      const imgHeightMm = (sourceHeight / pxPerMm);

      pdf.addImage(
        imgData,
        'JPEG',
        MARGIN_MM,
        MARGIN_MM,
        contentWidthMm,
        Math.min(imgHeightMm, contentHeightMm)
      );
    }

    pdf.save(filename);
    return { success: true, filename };
  } finally {
    document.body.removeChild(iframe);
  }
};

/**
 * Download como PDF
 * - Se conteúdo é HTML: converte para PDF usando html2canvas + jsPDF
 * - Se conteúdo é base64: baixa diretamente como PDF binário
 */
export const downloadAsPdf = async (artifact: Artifact): Promise<DownloadResult> => {
  try {
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.PDF);

    // Se o conteúdo é HTML, converte para PDF
    if (isHtmlContent(artifact.content)) {
      return await convertHtmlToPdf(artifact.content, filename);
    }

    // Caso contrário, trata como base64
    let base64Data = artifact.content;

    // Remove prefixo data URL se presente
    if (base64Data.includes('base64,')) {
      base64Data = base64Data.split('base64,')[1];
    }

    // Decodifica base64 para bytes
    const binaryString = atob(base64Data);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

    const blob = new Blob([bytes], { type: MIME_TYPES.pdf });
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como PDF',
    };
  }
};

// =============================================================================
// DOCX STRUCTURED TYPES
// =============================================================================

interface DocxCell {
  text: string;
  bold?: boolean;
}

interface DocxTableRow {
  cells: DocxCell[];
}

interface DocxParagraphElement {
  type: 'paragraph';
  text: string;
  heading?: 'TITLE' | 'HEADING_1' | 'HEADING_2' | 'HEADING_3';
  bold?: boolean;
  italics?: boolean;
  alignment?: 'center' | 'left' | 'right' | 'justify';
  size?: number;
  color?: string;
}

interface DocxTableElement {
  type: 'table';
  rows: DocxTableRow[];
}

type DocxElement = DocxParagraphElement | DocxTableElement;

interface DocxSection {
  children: DocxElement[];
}

interface DocxStructure {
  sections: DocxSection[];
}

// =============================================================================
// DOCX CONVERSION HELPERS
// =============================================================================

/**
 * Converte alinhamento para tipo do docx
 */
const getAlignment = (alignment?: string): (typeof AlignmentType)[keyof typeof AlignmentType] => {
  switch (alignment) {
    case 'center':
      return AlignmentType.CENTER;
    case 'right':
      return AlignmentType.RIGHT;
    case 'justify':
      return AlignmentType.JUSTIFIED;
    default:
      return AlignmentType.LEFT;
  }
};

/**
 * Converte heading para HeadingLevel
 */
const getHeadingLevel = (heading?: string): (typeof HeadingLevel)[keyof typeof HeadingLevel] | undefined => {
  switch (heading) {
    case 'TITLE':
      return HeadingLevel.TITLE;
    case 'HEADING_1':
      return HeadingLevel.HEADING_1;
    case 'HEADING_2':
      return HeadingLevel.HEADING_2;
    case 'HEADING_3':
      return HeadingLevel.HEADING_3;
    default:
      return undefined;
  }
};

/**
 * Cria um parágrafo DOCX a partir de elemento JSON
 */
const createDocxParagraph = (element: DocxParagraphElement): Paragraph => {
  const textRun = new TextRun({
    text: element.text || '',
    bold: element.bold,
    italics: element.italics,
    size: element.size,
    color: element.color,
  });

  return new Paragraph({
    children: [textRun],
    heading: getHeadingLevel(element.heading),
    alignment: getAlignment(element.alignment),
  });
};

/**
 * Cria uma tabela DOCX a partir de elemento JSON
 */
const createDocxTable = (element: DocxTableElement): Table => {
  const rows = element.rows.map(
    (row, rowIndex) =>
      new TableRow({
        children: row.cells.map(
          (cell) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: cell.text || '',
                      bold: cell.bold || rowIndex === 0, // Header row bold
                    }),
                  ],
                }),
              ],
              width: { size: 100 / row.cells.length, type: WidthType.PERCENTAGE },
            })
        ),
      })
  );

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

/**
 * Converte JSON estruturado para documento DOCX
 */
const convertJsonToDocx = async (content: string, title: string): Promise<Blob> => {
  // Parse JSON
  const data: DocxStructure = JSON.parse(content);

  // Coleta todos os elementos de todas as sections
  const children: (Paragraph | Table)[] = [];

  for (const section of data.sections || []) {
    for (const element of section.children || []) {
      if (element.type === 'table') {
        children.push(createDocxTable(element as DocxTableElement));
      } else {
        children.push(createDocxParagraph(element as DocxParagraphElement));
      }
    }
  }

  // Cria o documento
  const doc = new Document({
    title,
    sections: [
      {
        children,
      },
    ],
  });

  // Gera o blob
  return await Packer.toBlob(doc);
};

/**
 * Tenta reparar JSON truncado para conversão DOCX
 */
const tryRepairJsonForDocx = (content: string): string => {
  let repaired = content.trim();

  // Encontra o último elemento completo
  const lastValidEndings = [
    repaired.lastIndexOf('}]'),
    repaired.lastIndexOf('},'),
    repaired.lastIndexOf('"}'),
  ];

  const cutPoint = Math.max(...lastValidEndings);

  if (cutPoint > 0 && cutPoint < repaired.length - 2) {
    const endChar = repaired[cutPoint + 1];
    if (endChar === ']' || endChar === ',' || endChar === '}') {
      repaired = repaired.substring(0, cutPoint + 2);
    }
  }

  // Remove vírgula trailing
  repaired = repaired.replace(/,\s*$/, '');

  // Conta e fecha brackets/braces
  const openBrackets = (repaired.match(/\[/g) || []).length;
  const closeBrackets = (repaired.match(/\]/g) || []).length;
  const openBraces = (repaired.match(/\{/g) || []).length;
  const closeBraces = (repaired.match(/\}/g) || []).length;

  let remainingBrackets = openBrackets - closeBrackets;
  let remainingBraces = openBraces - closeBraces;

  while (remainingBrackets > 0 || remainingBraces > 0) {
    if (remainingBrackets > 0) {
      repaired += ']';
      remainingBrackets--;
    }
    if (remainingBraces > 0) {
      repaired += '}';
      remainingBraces--;
    }
  }

  return repaired;
};

/**
 * Download como DOCX
 * - Se conteúdo é JSON estruturado: converte para DOCX real
 * - Se conteúdo é base64: baixa diretamente
 */
export const downloadAsDocx = async (artifact: Artifact): Promise<DownloadResult> => {
  try {
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.DOCX);

    // Verifica se é JSON estruturado
    if (artifact.content.trim().startsWith('{')) {
      let jsonContent = artifact.content;

      // Tenta parsear, se falhar tenta reparar
      try {
        JSON.parse(jsonContent);
      } catch {
        jsonContent = tryRepairJsonForDocx(jsonContent);
      }

      // Converte JSON para DOCX
      const blob = await convertJsonToDocx(jsonContent, artifact.title);
      downloadBlob(blob, filename);
      return { success: true, filename };
    }

    // Se é base64, decodifica e baixa
    let base64Data = artifact.content;
    if (base64Data.includes('base64,')) {
      base64Data = base64Data.split('base64,')[1];
    }

    const binaryString = atob(base64Data);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

    const blob = new Blob([bytes], { type: MIME_TYPES.docx });
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como DOCX',
    };
  }
};

/**
 * Download como XLSX (quando conteúdo já é XLSX base64)
 */
export const downloadAsXlsx = (artifact: Artifact): DownloadResult => {
  try {
    let base64Data = artifact.content;

    // Remove prefixo data URL se presente
    if (base64Data.includes('base64,')) {
      base64Data = base64Data.split('base64,')[1];
    }

    // Decodifica base64 para bytes
    const binaryString = atob(base64Data);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

    const blob = new Blob([bytes], { type: MIME_TYPES.xlsx });
    const filename = generateFileName(artifact.title, DOWNLOAD_FORMATS.XLSX);
    downloadBlob(blob, filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao baixar como XLSX',
    };
  }
};

// =============================================================================
// MAIN DOWNLOAD FUNCTION
// =============================================================================

/**
 * Download de artefato no formato nativo
 * Determina automaticamente o formato baseado no tipo do artefato
 */
export const downloadArtifact = async (artifact: Artifact): Promise<DownloadResult> => {
  const format = getNativeFormat(artifact.type);

  switch (format) {
    case DOWNLOAD_FORMATS.TXT:
      return downloadAsText(artifact);

    case DOWNLOAD_FORMATS.MD:
      return downloadAsMarkdown(artifact);

    case DOWNLOAD_FORMATS.HTML:
      return downloadAsHtml(artifact);

    case DOWNLOAD_FORMATS.CSV:
      return downloadAsCsv(artifact);

    case DOWNLOAD_FORMATS.PDF:
      // Se é tipo PDF, baixa (suporta HTML e base64)
      if (artifact.type === ARTIFACT_TYPES.PDF) {
        return await downloadAsPdf(artifact);
      }
      // Para outros tipos (tax components), converte para texto
      return downloadAsText(artifact);

    case DOWNLOAD_FORMATS.DOCX:
      // Se é DOCX (binário ou estruturado), converte e baixa
      if (
        artifact.type === ARTIFACT_TYPES.DOCX ||
        artifact.type === ARTIFACT_TYPES.DOCX_STRUCTURED
      ) {
        return await downloadAsDocx(artifact);
      }
      // Fallback para texto
      return downloadAsText(artifact);

    case DOWNLOAD_FORMATS.XLSX:
      return downloadAsXlsx(artifact);

    default:
      // Fallback para texto
      return downloadAsText(artifact);
  }
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o formato nativo de download para um tipo de artefato
 */
export const getNativeFormat = (artifactType: string): DownloadFormat => {
  return NATIVE_FORMAT_MAP[artifactType] || DOWNLOAD_FORMATS.TXT;
};

/**
 * Retorna a extensão de arquivo para exibição
 */
export const getFileExtension = (artifactType: string): string => {
  const format = getNativeFormat(artifactType);
  return `.${format}`;
};

/**
 * Verifica se o tipo de artefato suporta download direto
 */
export const supportsDirectDownload = (artifactType: string): boolean => {
  return artifactType in NATIVE_FORMAT_MAP;
};
